import './ComplianceCategory.scss';

import { ComplianceCategories } from '@ui/icons';
import { ComplianceCategory } from '@sonar-software/sonar-core';
import { FilterComponent } from './FilterComponent';
import { getDirectParent } from '@lib/popups';

import { aggregateComplianceCategories, getFilterId, splitComplianceCategories } from '@ui/table-modifier/@arch/lib';
import { COPY_SPECIFIC, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { FilterOption, OptionFilter } from '@periscopes/general/filters';
import React, { useMemo } from 'react';

const complianceCategories = Object.entries(ComplianceCategory).filter(([ , id ]) => typeof id === 'number');

function geLabelOverride(value: string | ComplianceCategory): string | null {
	if (value === ComplianceCategory.Any) {
		return COPY_SPECIFIC.ANY_COMPLIANCE_CATEGORY;
	}

	if (value === ComplianceCategory.None) {
		return COPY_SPECIFIC.NO_COMPLIANCE_CATEGORIES;
	}

	return null;
}

const options: Array<FilterOption> = complianceCategories.map(([ label, value ]) => ({
	label: geLabelOverride(value) ?? label,
	value
}));

const transformSelection = (selected: Array<number>, previousSelection: Array<number>) => {
	if (!selected.length) {
		return undefined;
	}

	if (selected.includes(ComplianceCategory.None) && !previousSelection.includes(ComplianceCategory.None)) {
		return [ ComplianceCategory.None ];
	} else if (selected.includes(ComplianceCategory.Any) && !previousSelection.includes(ComplianceCategory.Any)) {
		return [ ComplianceCategory.Any ];
	}

	return selected.filter(
		(categoryId) => categoryId !== ComplianceCategory.None && categoryId !== ComplianceCategory.Any
	);
};

export const ComplianceCategoryFilter: FilterComponent<ComplianceCategory | undefined> = (props) => {
	const { format, id, label, onChange, value } = props;

	const selectedCategories = useMemo(() => splitComplianceCategories(value as number), [ value ]);

	return (
		<FilterComponent
			{ ...props }
			tagProps={ {
				clearValue: undefined,
				label: COPY_SPECIFIC.COMPLIANCE_CATEGORIES,
				value: selectedCategories
					.map((categoryId) =>
						geLabelOverride(categoryId) ?? ComplianceCategory[ categoryId ])
					.join(', ')
			} }>
			<OptionFilter
				className='filter'
				getPopupContainer={ getDirectParent }
				id={ getFilterId(String(id)) }
				maxTagCount='responsive'
				mode='multiple'
				onChange={ (newSelection: Array<number>) => {
					const selection = transformSelection(newSelection, selectedCategories);

					onChange({
						[ id ]: {
							format,
							value: selection ? aggregateComplianceCategories(selection) : undefined
						}
					});
				} }
				options={ options }
				placeholder={ PLACEHOLDERS_COPY.FILTER_BY(label) }
				showArrow
				tagRender={ (option) =>
					<ComplianceCategories
						categoryFlags={ Number(option.value) }
						direction='horizontal' /> }
				value={ selectedCategories } />
		</FilterComponent>
	);
};
